/**
 * [TM-03] API client โมดูลงานภายใน — ต่อ /api/v1/tasks/* ผ่าน wrapper เดิม (lib/api.ts)
 * shapes ทั้งหมดมาจาก @uwu/contracts (packages/contracts/src/tasks.ts) เท่านั้น — ห้ามนิยามซ้ำ
 * routes ตาม tasks/TM-CONTRACTS.md §3 (requireStaff ทุกเส้น · session mutation ส่ง X-Requested-With โดย wrapper)
 */
import { queryOptions } from "@tanstack/react-query";
import type {
  Attachment,
  TaskBoard,
  TaskChecklistItemCreate,
  TaskChecklistItemPatch,
  TaskComment,
  TaskCommentCreate,
  TaskCommentList,
  TaskCreate,
  TaskDetail,
  TaskEventList,
  TaskLabel,
  TaskLabelCreate,
  TaskLabelPatch,
  TaskList,
  TaskListQuery,
  TaskMember,
  TaskMemberAdd,
  TaskMyResponse,
  TaskPatch,
  TaskProjectCreate,
  TaskProjectDetail,
  TaskProjectList,
  TaskProjectPatch,
  TaskReport,
  TaskStatus,
  TaskStatusCreate,
  TaskStatusPatch,
  TaskTicketLinkCreate,
  TaskTicketLinkList,
  TaskTicketRef,
  TaskUserRef,
  TaskWatcherAdd,
} from "@uwu/contracts";
import { api, qs } from "./api";

/** รูปทรงของ GET /:taskId/checklist — เท่ากับ TaskDetail.checklist (สัญญาเดียวกัน) */
export type TaskChecklist = TaskDetail["checklist"];

const enc = encodeURIComponent;

// ---------- projects ----------

export function listProjects(): Promise<TaskProjectList> {
  return api.get<TaskProjectList>("/api/v1/tasks/projects");
}

export function createProject(body: TaskProjectCreate): Promise<TaskProjectDetail> {
  return api.post<TaskProjectDetail>("/api/v1/tasks/projects", body);
}

export function getProject(projectId: string): Promise<TaskProjectDetail> {
  return api.get<TaskProjectDetail>(`/api/v1/tasks/projects/${enc(projectId)}`);
}

export function patchProject(projectId: string, body: TaskProjectPatch): Promise<TaskProjectDetail> {
  return api.patch<TaskProjectDetail>(`/api/v1/tasks/projects/${enc(projectId)}`, body);
}

/** archive (soft delete) — manager เท่านั้น · 204 */
export function deleteProject(projectId: string): Promise<void> {
  return api.del<void>(`/api/v1/tasks/projects/${enc(projectId)}`);
}

// ---------- members ----------

export async function listMembers(projectId: string): Promise<TaskMember[]> {
  const r = await api.get<{ items: TaskMember[] }>(`/api/v1/tasks/projects/${enc(projectId)}/members`);
  return r.items;
}

export function addMembers(projectId: string, body: TaskMemberAdd): Promise<{ items: TaskMember[] }> {
  return api.post<{ items: TaskMember[] }>(`/api/v1/tasks/projects/${enc(projectId)}/members`, body);
}

/** manager หรือตัวเอง · ลบ manager คนสุดท้ายไม่ได้ (409 last_manager) · 204 */
export function removeMember(projectId: string, userId: string): Promise<void> {
  return api.del<void>(`/api/v1/tasks/projects/${enc(projectId)}/members/${enc(userId)}`);
}

// ---------- statuses / labels (manager) ----------

export function createStatus(projectId: string, body: TaskStatusCreate): Promise<TaskStatus> {
  return api.post<TaskStatus>(`/api/v1/tasks/projects/${enc(projectId)}/statuses`, body);
}

export function patchStatus(projectId: string, statusId: string, body: TaskStatusPatch): Promise<TaskStatus> {
  return api.patch<TaskStatus>(`/api/v1/tasks/projects/${enc(projectId)}/statuses/${enc(statusId)}`, body);
}

export function deleteStatus(projectId: string, statusId: string): Promise<void> {
  return api.del<void>(`/api/v1/tasks/projects/${enc(projectId)}/statuses/${enc(statusId)}`);
}

export function createLabel(projectId: string, body: TaskLabelCreate): Promise<TaskLabel> {
  return api.post<TaskLabel>(`/api/v1/tasks/projects/${enc(projectId)}/labels`, body);
}

export function patchLabel(projectId: string, labelId: string, body: TaskLabelPatch): Promise<TaskLabel> {
  return api.patch<TaskLabel>(`/api/v1/tasks/projects/${enc(projectId)}/labels/${enc(labelId)}`, body);
}

export function deleteLabel(projectId: string, labelId: string): Promise<void> {
  return api.del<void>(`/api/v1/tasks/projects/${enc(projectId)}/labels/${enc(labelId)}`);
}

// ---------- board / list ----------

/** done_days: 1..30 (default 7 — ค่าตายตัวที่ API ทำไว้) */
export function getBoard(projectId: string, doneDays?: number): Promise<TaskBoard> {
  return api.get<TaskBoard>(`/api/v1/tasks/projects/${enc(projectId)}/board${doneDays === undefined ? "" : qs({ done_days: doneDays })}`);
}

export function listTasks(projectId: string, query: TaskListQuery): Promise<TaskList> {
  return api.get<TaskList>(`/api/v1/tasks/projects/${enc(projectId)}/tasks${qs(query)}`);
}

// ---------- task ----------

export function createTask(projectId: string, body: TaskCreate): Promise<TaskDetail> {
  return api.post<TaskDetail>(`/api/v1/tasks/projects/${enc(projectId)}/tasks`, body);
}

export function getTask(taskId: string): Promise<TaskDetail> {
  return api.get<TaskDetail>(`/api/v1/tasks/${enc(taskId)}`);
}

/** conditional update — expected_version บังคับ (409 version_conflict เมื่อเก่า) */
export function patchTask(taskId: string, body: TaskPatch): Promise<TaskDetail> {
  return api.patch<TaskDetail>(`/api/v1/tasks/${enc(taskId)}`, body);
}

/** ผู้สร้าง หรือ manager · 204 */
export function deleteTask(taskId: string): Promise<void> {
  return api.del<void>(`/api/v1/tasks/${enc(taskId)}`);
}

// ---------- comments ----------

export async function listComments(taskId: string): Promise<TaskComment[]> {
  const r = await api.get<TaskCommentList>(`/api/v1/tasks/${enc(taskId)}/comments`);
  return r.items;
}

export function postComment(taskId: string, body: TaskCommentCreate): Promise<TaskComment> {
  return api.post<TaskComment>(`/api/v1/tasks/${enc(taskId)}/comments`, body);
}

// ---------- checklist ----------

export function listChecklist(taskId: string): Promise<TaskChecklist> {
  return api.get<TaskChecklist>(`/api/v1/tasks/${enc(taskId)}/checklist`);
}

export function createChecklistItem(taskId: string, body: TaskChecklistItemCreate): Promise<TaskChecklist[number]> {
  return api.post<TaskChecklist[number]>(`/api/v1/tasks/${enc(taskId)}/checklist`, body);
}

export function patchChecklistItem(taskId: string, itemId: string, body: TaskChecklistItemPatch): Promise<TaskChecklist[number]> {
  return api.patch<TaskChecklist[number]>(`/api/v1/tasks/${enc(taskId)}/checklist/${enc(itemId)}`, body);
}

export function deleteChecklistItem(taskId: string, itemId: string): Promise<void> {
  return api.del<void>(`/api/v1/tasks/${enc(taskId)}/checklist/${enc(itemId)}`);
}

// ---------- watchers ----------

export function addWatcher(taskId: string, body: TaskWatcherAdd): Promise<{ items: TaskUserRef[] }> {
  return api.post<{ items: TaskUserRef[] }>(`/api/v1/tasks/${enc(taskId)}/watchers`, body);
}

/** ตัวเอง หรือ manager · 204 */
export function removeWatcher(taskId: string, userId: string): Promise<void> {
  return api.del<void>(`/api/v1/tasks/${enc(taskId)}/watchers/${enc(userId)}`);
}

// ---------- events ----------

export async function listEvents(taskId: string): Promise<TaskEventList["items"]> {
  const r = await api.get<TaskEventList>(`/api/v1/tasks/${enc(taskId)}/events`);
  return r.items;
}

// ---------- my work ----------

/** GET /my — assigned/watching/created (cap 50 ต่อกลุ่ม) + overdue_count */
export function myWork(): Promise<TaskMyResponse> {
  return api.get<TaskMyResponse>("/api/v1/tasks/my");
}

// ---------- [TM-05] แนบไฟล์บนงาน (POST /api/v1/attachments ผ่าน helper เดิม — lib/attachments.ts) ----------

/** GET /:taskId/attachments — canRead ของโปรเจกต์ (server ตัดสิน) */
export async function listTaskAttachments(taskId: string): Promise<Attachment[]> {
  const r = await api.get<{ items: Attachment[] }>(`/api/v1/tasks/${enc(taskId)}/attachments`);
  return r.items;
}

/** DELETE /:taskId/attachments/:attachmentId — ผู้อัปโหลดหรือ manager · 204 */
export function deleteTaskAttachment(taskId: string, attachmentId: string): Promise<void> {
  return api.del<void>(`/api/v1/tasks/${enc(taskId)}/attachments/${enc(attachmentId)}`);
}

// ---------- [TM-05] การ์ดลูกค้าที่เชื่อม (task → ticket ทางเดียว) ----------

/** GET /:taskId/tickets — รายการลิงก์ (drawer ใช้ TaskDetail.tickets ก็ได้ — endpoint เดียวกันที่ serializer รวมมา) */
export async function listTaskTickets(taskId: string): Promise<TaskTicketRef[]> {
  const r = await api.get<TaskTicketLinkList>(`/api/v1/tasks/${enc(taskId)}/tickets`);
  return r.items;
}

/**
 * POST /:taskId/tickets — สร้างการ์ดลูกค้าแล้วลิงก์กับงาน (canWrite + ไม่ archived)
 * `Idempotency-Key` บังคับตามสัญญา (ไม่ส่ง = 422) — ผู้เรียกสร้าง key ต่อ "การเปิด dialog"
 * แล้วใช้ซ้ำเมื่อ retry ภายใน dialog เดียว (key+body เดิม = replay การ์ดเดิม · key เดิม+body ต่าง = 409)
 */
export function createTaskTicket(taskId: string, body: TaskTicketLinkCreate, idempotencyKey: string): Promise<TaskTicketLinkList> {
  return api.post<TaskTicketLinkList>(`/api/v1/tasks/${enc(taskId)}/tickets`, body, { headers: { "Idempotency-Key": idempotencyKey } });
}

// ---------- [TM-05] รายงานขั้นต่ำต่อโปรเจกต์ ----------

/** GET /tasks/reports?project_id= — canRead เท่านั้น (private non-member = 404) */
export function getTaskReport(projectId: string): Promise<TaskReport> {
  return api.get<TaskReport>(`/api/v1/tasks/reports${qs({ project_id: projectId })}`);
}

// ---------- react-query keys / options ----------

/** รากคีย์ของโมดูล — invalidateQueries({queryKey: ["tasks"]}) = ล้างทั้งโมดูล */
export const tasksRootKey = ["tasks"] as const;

export const tasksKeys = {
  projects: ["tasks", "projects"] as const,
  project: (id: string) => ["tasks", "project", id] as const,
  members: (id: string) => ["tasks", "members", id] as const,
  board: (id: string, doneDays: number) => ["tasks", "board", id, doneDays] as const,
  list: (id: string, query: TaskListQuery) => ["tasks", "list", id, query] as const,
  task: (id: string) => ["tasks", "task", id] as const,
  comments: (id: string) => ["tasks", "comments", id] as const,
  events: (id: string) => ["tasks", "events", id] as const,
  attachments: (id: string) => ["tasks", "attachments", id] as const,
  tickets: (id: string) => ["tasks", "tickets", id] as const,
  report: (id: string) => ["tasks", "report", id] as const,
  my: ["tasks", "my"] as const,
};

/** [TM-CONTRACTS §3] GET /projects — company ที่ยังไม่ archived + ที่ตนเป็นสมาชิก */
export const tasksProjectsQuery = queryOptions({
  queryKey: tasksKeys.projects,
  queryFn: listProjects,
  retry: false,
});

/** [TM-CONTRACTS §3] GET /my — assigned/watching/created + overdue_count */
export const tasksMyQuery = queryOptions({
  queryKey: tasksKeys.my,
  queryFn: myWork,
  retry: false,
});

// ---------- search params ของ /app/tasks/:projectId (route-level — ตาม pattern ของ reports) ----------

export type TasksSearch = { view: "board" | "list" | "report"; done_days: number; task?: string };

/** whitelist ค่า search — done_days 1..30 (default 7), view board|list|report (default board), task = เปิด drawer */
export function validateTasksSearch(s: Record<string, unknown>): TasksSearch {
  const n = Number(s.done_days);
  const done_days = Number.isInteger(n) && n >= 1 && n <= 30 ? n : 7;
  const view = s.view === "list" || s.view === "report" ? s.view : ("board" as const);
  const task = typeof s.task === "string" && s.task ? s.task : undefined;
  return { view, done_days, task };
}
